import { Patient } from "@/types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { usePatientsStore } from "@/lib/stores/patients";
import { useDoctorsStore } from "@/lib/stores/doctors";
import { usePrescriptionsStore } from "@/lib/stores/prescriptions";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Clock, MoreVertical, User, UserCheck, MapPin, UserPlus, AlertCircle, Calendar, Printer } from "lucide-react";
import { safeFormatDistance } from '@/lib/utils/dateUtils';
import { printPatientOPSheet } from '@/lib/utils/printUtils';
import { printPrescription } from '@/lib/utils/printPrescription';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Select,
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { WhatsAppShareButton } from "@/components/shared/WhatsAppShareButton";

interface PatientCardProps {
  patient: Patient;
  showActions?: boolean;
  showAppointmentTime?: boolean;
}

export function PatientCard({ 
  patient, 
  showActions = true, 
  showAppointmentTime = false 
}: PatientCardProps) {
  const { toast } = useToast();
  const { updatePatientDoctor, updatePatientStatus } = usePatientsStore();
  const { getPrescriptionsByPatient, prescriptions } = usePrescriptionsStore();
  const doctors = useDoctorsStore((state) => state.doctors);

  const [selectedDoctor, setSelectedDoctor] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [hasPrescriptions, setHasPrescriptions] = useState(false);
  const [hasIssuedPrescriptions, setHasIssuedPrescriptions] = useState(false);

  // Check if patient has any prescriptions
  useEffect(() => {
    const patientPrescriptions = getPrescriptionsByPatient(patient.id);
    console.log(`Patient ${patient.id} prescriptions:`, patientPrescriptions);
    setHasPrescriptions(patientPrescriptions.length > 0);
    setHasIssuedPrescriptions(patientPrescriptions.some(p => p.status === 'issued'));
  }, [patient.id, prescriptions, getPrescriptionsByPatient]);

  const handleDoctorAssign = () => {
    if (!selectedDoctor || selectedDoctor === "no_doctors_available") {
      toast({
        title: "Error",
        description: "Please select a doctor",
        variant: "destructive"
      });
      return;
    }

    try {
      // First update the patient's doctor
      updatePatientDoctor(patient.id, selectedDoctor);
      
      // Then update the patient's status with the selected doctor's ID
      updatePatientStatus(patient.id, "in-consultation", selectedDoctor);
      
      toast({
        title: "Success",
        description: "Doctor assigned successfully"
      });
      setOpen(false);
      setSelectedDoctor(""); // Reset the selection after successful assignment
    } catch (error) {
      console.error("Error assigning doctor:", error);
      toast({
        title: "Error",
        description: "Failed to assign doctor",
        variant: "destructive"
      });
    }
  };

  // Show all doctors (no status filtering)
  const availableDoctors = doctors;

  const getPriorityBadge = () => {
    if (patient.priority === "urgent") {
      return (
        <Badge variant="outline" className="bg-hospital-warning/10 text-hospital-warning border-hospital-warning">
          Urgent
        </Badge>
      );
    }
    if (patient.priority === "emergency") {
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 font-semibold">
          Emergency
        </Badge>
      );
    }
    return null;
  };

  const getStatusBadge = () => {
    switch (patient.status) {
      case "waiting":
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            Waiting
          </Badge>
        );
      case "in-consultation":
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            In Consultation
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            Completed
          </Badge>
        );
      case "no-show":
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            No Show
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatAppointmentTime = (isoTime: string) => {
    const date = new Date(isoTime);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <Card className="mb-3 overflow-hidden hover:shadow-md transition-shadow">
      <CardContent className="p-3">
        {/* Mobile Layout */}
        <div className="block sm:hidden space-y-3">
          {/* Header with name and status */}
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <Link to={`/patient/${patient.id}`} className="hover:text-hospital-primary transition-colors">
                <h3 className="text-base font-medium">{patient.name}</h3>
              </Link>
              <div className="flex items-center gap-2 mt-1">
                {getPriorityBadge()}
                {getStatusBadge()}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {/* Smart Print Button - OP Sheet or Prescription */}
              {hasIssuedPrescriptions ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const prescriptions = getPrescriptionsByPatient(patient.id);
                    printPrescription(patient, prescriptions, doctors);
                  }}
                  className="border-gray-300 text-gray-600 hover:bg-gray-50 text-xs"
                  title="Print Prescription"
                >
                  <Printer className="h-3 w-3 mr-1" />
                  <span className="hidden">Print</span>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => printPatientOPSheet(patient, doctors)}
                  className="border-gray-300 text-gray-600 hover:bg-gray-50 text-xs"
                  title="Print OP Sheet"
                >
                  <Printer className="h-3 w-3 mr-1" />
                  <span className="hidden">Print</span>
                </Button>
              )}
              
              {showActions && patient.status === "waiting" && (
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-hospital-primary text-hospital-primary hover:bg-hospital-primary/10 text-xs"
                    >
                      Assign
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Assign Doctor</DialogTitle>
                      <DialogDescription>
                        Select a doctor to assign to this patient.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Select 
                        value={selectedDoctor} 
                        onValueChange={setSelectedDoctor}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a doctor" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDoctors.length > 0 ? (
                            availableDoctors.map((doctor) => (
                              <SelectItem key={doctor.id} value={doctor.id}>
                                {doctor.name} - {doctor.specialization}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no_doctors_available" disabled>
                              No doctors available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <Button 
                        className="mt-4 w-full"
                        onClick={handleDoctorAssign}
                      >
                        Assign Doctor
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>

          {/* Patient Info */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
              <User className="h-3.5 w-3.5" />
              <span>{patient.age} • {patient.gender}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
              <UserCheck className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{patient.contactNumber}</span>
              <WhatsAppShareButton
                phoneNumber={patient.contactNumber}
                patientName={patient.name}
                patientId={patient.id}
                reason={patient.reason}
                doctorName={doctors.find((d) => d.id === patient.doctorId)?.name}
                showLabel={false}
                className="h-7 w-7"
              />
            </div>
            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
              <Clock className="h-3.5 w-3.5" />
              <span>{safeFormatDistance(patient.registeredAt)}</span>
            </div>
          </div>

          {/* Additional Info */}
          <div className="space-y-2">
            {showAppointmentTime && patient.appointmentTime && (
              <div className="flex items-center gap-1.5 text-blue-600 text-sm">
                <Clock className="h-3.5 w-3.5" />
                <span>Appt: {formatAppointmentTime(patient.appointmentTime)}</span>
              </div>
            )}
            {patient.followUpDate && (
              <div className="flex items-center gap-1.5 text-green-600 text-sm">
                <Calendar className="h-3.5 w-3.5" />
                <span>Follow-up: {new Date(patient.followUpDate).toLocaleDateString()}</span>
              </div>
            )}
            {patient.referralDoctor && (
              <div className="flex items-center gap-1.5 text-gray-600 text-sm">
                <UserPlus className="h-3.5 w-3.5" />
                <span>Ref: {patient.referralDoctor}</span>
              </div>
            )}
            {patient.location && (
              <div className="flex items-center gap-1.5 text-gray-600 text-sm">
                <MapPin className="h-3.5 w-3.5" />
                <span>{patient.location}</span>
              </div>
            )}
            <div className="text-gray-600 text-sm">
              <span className="font-medium">Reason: </span>
              {patient.reason}
            </div>
            {(() => {
              const servicesCost = patient.services?.reduce((total, service) => total + service.price, 0) || 0;
              const totalAmount = servicesCost;
              const amountPaid = patient.amountPaid || 0;
              
              // For completed patients with no services (bill generated), show "Bills Cleared"
              if (patient.status === 'completed' && servicesCost === 0) {
                return (
                  <div className="text-white font-bold text-sm bg-blue-500 px-2 py-0.5 rounded-full inline-block">
                    Bills Cleared
                  </div>
                );
              }
              
              // If no payment has been entered at all (amountPaid is null/undefined/0 and no services)
              if ((amountPaid === 0 || amountPaid === null || amountPaid === undefined) && servicesCost === 0) {
                return null; // Don't show payment information
              }
              
              if (amountPaid >= totalAmount && totalAmount > 0) {
                return (
                  <div className="text-green-600 font-medium text-sm">
                    Paid: ₹{amountPaid}
                  </div>
                );
              } else if (totalAmount > 0) {
                return (
                  <div className="text-red-600 font-medium text-sm">
                    Due: ₹{totalAmount - amountPaid}
                  </div>
                );
              }
              
              return null; // Don't show anything if no services and no payment
            })()}
          </div>

          {/* Services */}
          {patient.services && patient.services.length > 0 && (
            <div className="border-t pt-2">
              <div className="flex flex-wrap gap-1.5">
                {patient.services.map((service, index) => (
                  <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    {service.service_name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:grid grid-cols-12 gap-4">
          {/* Left Section - Basic Info */}
          <div className="col-span-3">
            <div className="space-y-2">
              <Link to={`/patient/${patient.id}`} className="hover:text-hospital-primary transition-colors">
                <h3 className="text-base font-medium">{patient.name}</h3>
              </Link>
              <div className="flex items-center gap-1.5 text-gray-600">
                <User className="h-3.5 w-3.5" />
                <span>{patient.age} • {patient.gender}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <UserCheck className="h-3.5 w-3.5" />
                <span>{patient.contactNumber}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <Clock className="h-3.5 w-3.5" />
                <span>{safeFormatDistance(patient.registeredAt)}</span>
              </div>
            </div>
          </div>

          {/* Middle Section */}
          <div className="col-span-7">
            {/* Top Row - Additional Info and Status */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              {/* Left Column - Additional Info */}
              <div className="space-y-2">
                {showAppointmentTime && patient.appointmentTime && (
                  <div className="flex items-center gap-1.5 text-blue-600">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Appt: {formatAppointmentTime(patient.appointmentTime)}</span>
                  </div>
                )}
                {patient.followUpDate && (
                  <div className="flex items-center gap-1.5 text-green-600">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Follow-up: {new Date(patient.followUpDate).toLocaleDateString()}</span>
                  </div>
                )}
                {patient.referralDoctor && (
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <UserPlus className="h-3.5 w-3.5" />
                    <span>Ref: {patient.referralDoctor}</span>
                  </div>
                )}
                {patient.location && (
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>{patient.location}</span>
                  </div>
                )}
                <div className="text-gray-600">
                  <span className="font-medium">Reason: </span>
                  {patient.reason}
                </div>
                {(() => {
                  const servicesCost = patient.services?.reduce((total, service) => total + service.price, 0) || 0;
                  const totalAmount = servicesCost;
                  const amountPaid = patient.amountPaid || 0;
                  
                  // For completed patients with no services (bill generated), show "Bills Cleared"
                  if (patient.status === 'completed' && servicesCost === 0) {
                    return (
                      <div className="text-white font-bold text-sm bg-blue-500 px-2 py-0.5 rounded-full inline-block">
                        Bills Cleared
                      </div>
                    );
                  }
                  
                  if (amountPaid >= totalAmount) {
                    return (
                      <div className="text-green-600 font-medium">
                        Paid: ₹{amountPaid}
                      </div>
                    );
                  } else {
                    return (
                      <div className="text-red-600 font-medium">
                        Due: ₹{totalAmount - amountPaid}
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Right Column - Status Indicators */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {getPriorityBadge()}
                  {getStatusBadge()}
                </div>
              </div>
            </div>

            {/* Bottom Row - Services */}
            {patient.services && patient.services.length > 0 && (
              <div className="border-t pt-2">
                <div className="flex flex-wrap gap-1.5">
                  {patient.services.map((service, index) => (
                    <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                      {service.service_name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Section - Actions */}
          <div className="col-span-2">
            <div className="flex flex-col items-end gap-2">
              {/* Smart Print Button - OP Sheet or Prescription */}
              {hasIssuedPrescriptions ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const prescriptions = getPrescriptionsByPatient(patient.id);
                    printPrescription(patient, prescriptions, doctors);
                  }}
                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                  title="Print Prescription"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print Prescription
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => printPatientOPSheet(patient, doctors)}
                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                  title="Print OP Sheet"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print OP Sheet
                </Button>
              )}
              
              {showActions && patient.status === "waiting" && (
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-hospital-primary text-hospital-primary hover:bg-hospital-primary/10"
                    >
                      Assign
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Assign Doctor</DialogTitle>
                      <DialogDescription>
                        Select a doctor to assign to this patient.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      <Select 
                        value={selectedDoctor} 
                        onValueChange={setSelectedDoctor}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a doctor" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableDoctors.length > 0 ? (
                            availableDoctors.map((doctor) => (
                              <SelectItem key={doctor.id} value={doctor.id}>
                                {doctor.name} - {doctor.specialization}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no_doctors_available" disabled>
                              No doctors available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <Button 
                        className="mt-4 w-full"
                        onClick={handleDoctorAssign}
                      >
                        Assign Doctor
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
